import { useCallback, useEffect, useState } from 'react';
import Pagination from '../../components/pagination/Pagination';
import './supBooking.css';
import { bookingCount, fetchSupBooking, getTrips } from './supBookingFun/fetchSupBookings';
import ReactLoading from 'react-loading';
import EmptyPage from '../../components/empty-page/emptyPage';
import fetchTripDates from './supBookingFun/fetchTripDates';
import Dialog from '../../components/dialogModal/dialogModal';
import { sendMessage } from './supBookingFun/sendMessage';
import { standardDate } from '../../utils/formatDate';
import { getOrganizers } from '../../utils/getOrganizers';
import { truncateText } from '../../utils/maskFields';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supBookingsServiceConfig } from '../../utils/supportServiceConfig/supBookingsServiceConfig';
import SupBookingAct from './bookingTypes/SupBookingAct';
import SupBookingGu from './bookingTypes/SupBookingGu';
import SupBookingStay from './bookingTypes/SupBookingStay';
import { getTransationCharges } from '../../utils/getAmountWithTransaction';
import BookingStats from './bookingStats/BookingStats';

const MODE_TO_SERVICE = {
    t: 'trip',
    a: 'act',
    c: 'camp',
    g: 'gu'
};

const SupBooking = () => {
    const { orgId, mode } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const serviceType = MODE_TO_SERVICE[mode] || 'trip';
    const selectedType = searchParams.get('type') || 'upcoming';
    const datesType = searchParams.get('datesType') || 'fixed';
    const group = searchParams.get('group') || 'backpacking';
    const selectedTrip = searchParams.get('trip') || '';
    const selectedDate = searchParams.get('date') || '';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [trips, setTrips] = useState([]);
    const [stats, setStats] = useState({});
    const selectedOrg = orgId || '';
    const [orgData, setOrgData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tripDates, setTripDates] = useState([]);
    const [modal, setModal] = useState(false);
    const [rowData, setRowData] = useState({});
    const [messageType, setMessageType] = useState([]);
    const [messageData, setMessageData] = useState({
        msgType: 'whatsApp',
        whatsApp: '',
        message: ''
    });
    const [totalCount, setTotalCount] = useState(0);
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);
    const tableHeader = [
        'Trip Name',
        orgId ? 'Name' : 'Organizer',
        'Start City',
        'Pick Up',
        'Booked On',
        'Payment',
        'Seats',
        'Rooms',
        'Transport',
        'Booked Via',
        'People',
        'Ticket Status'
    ];

    const customTabelHeadre = [
        'Trip Name',
        'Name',
        'Start City',
        'Pick Up',
        'Booked On',
        'Seats',
        'Rooms',
        'Bed',
        'Payment',
        'IsGroup',
        'Vehicle',
        'Booked Via',
        'People'
    ];

    const dummyMessageType = [
        { value: 'whatsApp', name: 'WhatsApp' },
        { value: 'rate', name: 'Rate' },
        { value: 'custom', name: 'Custom' }
    ];
    const [rowClickId, setRowClickId] = useState(0);
    // camp: reload after a cancel / manual booking, and the manual booking form
    const [refreshKey, setRefreshKey] = useState(0);
    const reload = () => setRefreshKey((k) => k + 1);

    // a booking added from the header's "Add Booking" (AddBookingSup) — show it
    useEffect(() => {
        const onAdded = () => setRefreshKey((k) => k + 1);
        window.addEventListener('sup-booking-added', onAdded);
        return () => window.removeEventListener('sup-booking-added', onAdded);
    }, []);
    const [customRowClickId, setCustomRowClickId] = useState(0);
    const serviceBookingConfig = supBookingsServiceConfig[serviceType];

    const SERVICE_TO_MODE = {
        trip: 't',
        act: 'a',
        camp: 'c',
        gu: 'g'
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    const handleServiceType = (event) => {
        const selectedValue = event.target.value;
        const newMode = SERVICE_TO_MODE[selectedValue];

        if (!newMode) return;

        const params = new URLSearchParams();

        // Only keep common filters
        params.set('type', 'upcoming');
        params.set('page', '1');

        navigate(`/support/${newMode}/bookings?${params.toString()}`);
    };

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        setBookings([]);
        setTrips([]);
        setTripDates([]);
        setTotalCount(0);
    }, [serviceType]);

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    useEffect(() => {
        const fetchOrg = async () => {
            serviceBookingConfig.fetchOrg({ serviceType, setOrgData, setLoading });
        };
        fetchOrg();
    }, [serviceType]);

    useEffect(() => {
        const fetchBookingData = async () => {
            serviceBookingConfig.fetchBookingData({
                limit,
                setBookings,
                setStats,
                setLoading,
                offSet,
                selectedOrg,
                selectedTrip,
                selectedDate,
                selectedType,
                datesType,
                group,
                serviceType
            });

            serviceBookingConfig.fetchTrips({
                selectedOrg,
                setTrips,
                datesType,
                group,
                setLoading,
                serviceType
            });
        };

        fetchBookingData();

        setMessageType(dummyMessageType);

        // eslint-disable-next-line
    }, [selectedType, selectedTrip, selectedDate, selectedOrg, offSet, limit, datesType, group, serviceType, refreshKey]);

    useEffect(() => {
        if (!selectedTrip || !selectedOrg || !serviceBookingConfig.fetchTripDates) return;
        serviceBookingConfig.fetchTripDates({ selectedOrg, selectedTrip, setTripDates, selectedType, setLoading });
    }, [selectedType, selectedTrip, selectedOrg, datesType, serviceType]);

    useEffect(() => {
        // if (!selectedOrg) return;
        serviceBookingConfig.fetchBookingCount({
            setTotalCount,
            setLoading,
            selectedOrg,
            selectedTrip,
            selectedDate,
            selectedType,
            datesType,
            group,
            serviceType
        });
    }, [selectedType, selectedTrip, selectedDate, selectedOrg, datesType, group, serviceType, refreshKey]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        if (rowClickId !== 0) {
            window.open(`/support/${mode}/trip/date/${selectedOrg}/${rowClickId}`, '_blank');
            setRowClickId(0);
        }
        //eslint-disable-next-line
    }, [rowClickId]);

    useEffect(() => {
        if (customRowClickId !== 0) {
            const bookingsById = bookings.find((bookingsItem) => bookingsItem?._id === customRowClickId);
            const packId = bookingsById?.packId?._id;
            const startDate = bookingsById?.startDate.split('T')[0];
            window.open(`/support/${mode}/trip/date/${selectedOrg}/${packId}/${startDate}`);
            setCustomRowClickId(0);
        }
        //eslint-disable-next-line
    }, [customRowClickId]);

    const getTripDetails = (row) => {
        return (
            <div className='bookingTitleArea'>
                <div>{row?.packId?.title ?? 'N/A'}</div>
                <div>
                    {standardDate(row.startDate.split('T')[0])} (
                    {`${row?.packId?.days ?? '(N/A)'}D/${row?.packId?.nights ?? '(N/A)'}N`})
                </div>
            </div>
        );
    };

    const getUserDetails = (row) => {
        return row.userDetails.map(function (user, userKey) {
            return (
                <div key={userKey}>
                    <span>{user?.name ?? 'N/A'}, </span>
                    <span>{user?.age ?? 'N/A'}, </span>
                    <span>{user?.sex ?? 'N/A'}, </span>
                    <span>{user?.phoneNumber ?? 'N/A'}</span>
                </div>
            );
        });
    };

    // const visibleTableHeader =
    //     selectedType === 'upcoming' ? tableHeader : tableHeader.filter((header) => header !== 'Action');

    return (
        <>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='bookingSticky'>
                <div className='alignCenter'>
                    <h2 className='orgBookingHeading'>Bookings</h2>
                    <div className='orgFilterWrapper'>
                        {/* <div className='orgTripDateInfo mb-2'>
                            <span className='orgTripDateSpan'>Bookings Today</span>
                            <span className='orgTripDateSpan'>{todaysBookings.bookedToday ?? '0'}</span>
                        </div> */}
                    </div>
                </div>
                <BookingStats stats={stats} />
                <div className='orgBookingFilters'>
                    <select
                        name='serviceType'
                        id='serviceType'
                        className='select mr-1'
                        onChange={handleServiceType}
                        value={serviceType}
                    >
                        <option value='trip'>Trip</option>
                        <option value='act'>Activity</option>
                        <option value='gu'>Guide</option>
                        <option value='camp'>Camp</option>
                    </select>
                    <select
                        name='org'
                        id='org'
                        className='select'
                        value={selectedOrg}
                        onChange={(e) => {
                            const selectedId = e.target.value;

                            if (selectedId) {
                                const params = new URLSearchParams();
                                params.set('type', selectedType);
                                // add datesType only for trip
                                if (mode === 't') {
                                    params.set('datesType', datesType);
                                    params.set('group', group);
                                }
                                params.set('page', 1);
                                navigate(`/support/${mode}/bookings/${selectedId}?${params.toString()}`);
                            }
                        }}
                    >
                        <option value=''>Select Organizer</option>
                        {orgData.map((org) => (
                            <option value={org._id} key={org._id}>
                                {org.companyName}
                            </option>
                        ))}
                    </select>
                    <select
                        name='type'
                        id='type'
                        className='select'
                        value={selectedType}
                        onChange={(e) => {
                            const value = e.target.value;
                            updateURL({ type: value, page: 1 });
                        }}
                    >
                        <option value='upcoming'>Upcoming</option>
                        <option value='completed'>Completed</option>
                    </select>
                    {serviceType === 'trip' && (
                        <>
                            <select
                                name='datesType'
                                id='datesType'
                                className='select'
                                value={datesType}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    updateURL({ datesType: value, page: 1 });
                                }}
                            >
                                <option value='fixed'>Fixed</option>
                                <option value='custom'>Custom</option>
                            </select>
                            <select
                                name='datesType'
                                id='datesType'
                                className='select'
                                value={group}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    updateURL({ group: value, page: 1 });
                                }}
                            >
                                <option value='backpacking'>Backpacking</option>
                                <option value='vehicle'>Vehicle</option>
                            </select>
                        </>
                    )}

                    {selectedType && selectedOrg && (
                        <select
                            name='trip'
                            id='trip'
                            className='select'
                            value={selectedTrip}
                            onChange={(e) => {
                                const value = e.target.value;
                                updateURL({ trip: value, page: 1 });
                            }}
                        >
                            <option value=''>{serviceType === 'camp' ? 'Select Stay' : 'Select Trip'}</option>
                            {trips.map((trip) => (
                                <option value={trip._id} key={trip._id}>
                                    {trip.title}
                                </option>
                            ))}
                        </select>
                    )}
                    {serviceType === 'trip' && selectedType && selectedTrip && datesType === 'fixed' && (
                        <select
                            name='date'
                            id='date'
                            className='select'
                            value={selectedDate}
                            onChange={(e) => {
                                updateURL({ date: e.target.value, page: 1 });
                            }}
                        >
                            <option value=''>Select Date</option>
                            {tripDates.map((date) => (
                                <option value={date._id} key={date._id}>
                                    {date.startDate.split('T')[0]}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
            <div className='orgBookingContainer'>
                {bookings?.length === 0 && !loading ? (
                    <EmptyPage />
                ) : (
                    <>
                        {/* TRIP TABLE */}
                        {serviceType === 'trip' && (
                            <div className='tableContainer'>
                                <table className='table'>
                                    <thead className='tableHead'>
                                        <tr>
                                            {(datesType === 'fixed' ? tableHeader : customTabelHeadre).map(
                                                (head, headIndex) => (
                                                    <th key={headIndex}>{head}</th>
                                                )
                                            )}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {bookings?.map((row, rowIndex) => {
                                            const status = row?.tripStatus; // you can make dynamic later

                                            return (
                                                <tr
                                                    key={rowIndex}
                                                    className={'tableBodyRow allowCLick'}
                                                    onClick={() =>
                                                        datesType === 'fixed'
                                                            ? setRowClickId(row?.packDateId)
                                                            : setCustomRowClickId(row?._id)
                                                    }
                                                >
                                                    {datesType === 'fixed' ? (
                                                        <>
                                                            <td>{getTripDetails(row)}</td>
                                                            <td>
                                                                {orgId
                                                                    ? row?.userDetails?.[0]?.name
                                                                    : row?.organizerId?.companyName}
                                                            </td>
                                                            <td>{row.startCity ?? 'N/A'}</td>
                                                            <td>{truncateText(row.pickupLocation, 40) ?? 'N/A'}</td>
                                                            <td>{standardDate(row.createdAt.split('T')[0])}</td>

                                                            <td
                                                                style={{
                                                                    color:
                                                                        row?.amountPaid?.isReserved ||
                                                                        row?.amountPaid?.isInstallment
                                                                            ? 'red'
                                                                            : '',
                                                                    textAlign: 'center',
                                                                    whiteSpace: 'pre-line',
                                                                    verticalAlign: 'middle'
                                                                }}
                                                            >
                                                                {row?.amountPaid?.isReserved ||
                                                                row?.amountPaid?.isInstallment
                                                                    ? `${row?.amountPaid?.isReserved ? 'Reserved' : 'Installment'}\n${
                                                                          row?.amountPaid?.isReserved
                                                                              ? `(₹${row?.amountPaid?.reserve}/seat)`
                                                                              : row?.amountPaid?.isInstallment
                                                                                ? `(₹${row?.amountPaid?.installments?.[0]?.amount})`
                                                                                : ''
                                                                      }
                                              Total: ${row?.amountPaid?.total}
                                              Paid: ${row?.amountPaid?.paid - (row?.amountPaid?.paid > row?.amountPaid?.reserve * row?.totalSeats ? getTransationCharges(row?.amountPaid?.reserve * row?.totalSeats) : 0)}
                                              Balance: ${row?.amountPaid?.total - row?.amountPaid?.reserve * row?.totalSeats}`
                                                                    : <>Full payment<br />Paid: ₹{Number(row?.amountPaid?.paid) || 0}</>}
                                                            </td>

                                                            <td>{row.totalSeats}</td>
                                                            <td>{row.totalRooms}</td>
                                                            <td>
                                                                {row.transport === 'With transport'
                                                                    ? 'Included'
                                                                    : 'Excluded'}
                                                            </td>
                                                            <td>{row.bookedVia ?? 'N/A'}</td>
                                                            <td>{getUserDetails(row)}</td>

                                                            <td
                                                                style={
                                                                    status === 'Cancelled'
                                                                        ? { color: 'red', fontWeight: '600' }
                                                                        : {}
                                                                }
                                                            >
                                                                {status}
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td>{getTripDetails(row)}</td>
                                                            <td>{row?.userDetails?.[0]?.name}</td>
                                                            <td>{row.startCity ?? 'N/A'}</td>
                                                            <td>{row.pickupLocation ?? 'N/A'}</td>
                                                            <td>{standardDate(row?.createdAt?.split('T')[0])}</td>
                                                            <td>{row?.totalSeats ?? '0'}</td>
                                                            <td>{row?.customTrip?.rooms?.length ?? '0'}</td>
                                                            <td>{row?.customTrip?.bed ?? '0'}</td>

                                                            <td
                                                                style={{
                                                                    color:
                                                                        row?.amountPaid?.isReserved ||
                                                                        row?.amountPaid?.isInstallment
                                                                            ? 'red'
                                                                            : '',
                                                                    textAlign: 'center',
                                                                    whiteSpace: 'pre-line',
                                                                    verticalAlign: 'middle'
                                                                }}
                                                            >
                                                                {row?.amountPaid?.isReserved ||
                                                                row?.amountPaid?.isInstallment
                                                                    ? `${row?.amountPaid?.isReserved ? 'Reserved' : 'Installment'}\n${
                                                                          row?.amountPaid?.isReserved
                                                                              ? `(₹${row?.amountPaid?.reserve}/seat)`
                                                                              : row?.amountPaid?.isInstallment
                                                                                ? `(₹${row?.amountPaid?.installments?.[0]?.amount})`
                                                                                : ''
                                                                      }
                                              Total: ${row?.amountPaid?.total}
                                              Paid: ${row?.amountPaid?.paid}
                                              Balance: ${row?.amountPaid?.total - row?.amountPaid?.reserve * row?.totalSeats}`
                                                                    : <>Full payment<br />Paid: ₹{Number(row?.amountPaid?.paid) || 0}</>}
                                                            </td>

                                                            <td>{row?.customTrip?.isGroup ? 'Yes' : 'No'}</td>
                                                            <td>{row?.transport ?? 'N/A'}</td>
                                                            <td>{row?.bookedVia ?? 'N/A'}</td>
                                                            <td>{getUserDetails(row)}</td>
                                                        </>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* RESPONSIVE CARDS */}
                                <div className='responsiveCardContainer'>
                                    {bookings?.map((row, rowIndex) => (
                                        <div key={rowIndex} className='responsiveCard'>
                                            <div className='cardContent'>
                                                <p>
                                                    <strong>{tableHeader[0]}:</strong> {getTripDetails(row)}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[1]}:</strong> {getUserDetails(row)}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[2]}:</strong> {row.startCity ?? 'N/A'}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[3]}:</strong> {row.pickupLocation ?? 'N/A'}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[4]}:</strong>
                                                    {row.transport === 'With transport' ? 'Included' : 'Excluded'}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[5]}:</strong> {row.totalSeats ?? 'N/A'}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[6]}:</strong>
                                                    {standardDate(row.createdAt.split('T')[0])}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[7]}:</strong> {row.bookedVia ?? 'N/A'}
                                                </p>
                                                <p>
                                                    <strong>{tableHeader[8]}:</strong> {row.tripStatus ?? 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ACTIVITY */}
                        {serviceType === 'act' && <SupBookingAct bookingData={bookings} />}
                        {serviceType === 'gu' && <SupBookingGu bookingData={bookings} orgId={orgId} />}
                        {serviceType === 'camp' && <SupBookingStay bookingData={bookings} onChanged={reload} />}
                    </>
                )}
                <Pagination
                    paginationClick={paginationClick}
                    numberOfRows={totalData}
                    offSet={offSet}
                    pageNumber={pageNumber}
                    limit={limit}
                />
                {modal && (
                    <Dialog openModal={modal} heading={'Send Message'}>
                        <div className='dialogContent'>
                            <select
                                name='date'
                                id='date'
                                className='select w-full'
                                value={'whatsApp'}
                                onChange={(e) =>
                                    setMessageData({
                                        ...messageData,
                                        msgType: e.target.value
                                    })
                                }
                                disabled
                            >
                                <option value=''>Select Message Type</option>
                                {messageType.map((type) => (
                                    <option value={type.value} key={type.value}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                            {messageData.msgType === 'whatsApp' && (
                                <input
                                    placeholder='Whatsapp group link'
                                    name='whatsapp'
                                    onChange={(e) =>
                                        setMessageData({
                                            ...messageData,
                                            whatsApp: e.target.value
                                        })
                                    }
                                    value={messageData.whatsApp}
                                    className='whatsAppLink'
                                />
                            )}
                            {/* {messageData.msgType !== 'rate' &&
                                messageData.msgType !== 'whatsApp' && (
                                    <>
                                        <textarea
                                            className="messageArea"
                                            value={messageData.message}
                                            onChange={(e) =>
                                                setMessageData({
                                                    ...messageData,
                                                    message: e.target.value
                                                })
                                            }
                                        />
                                        <CharCount
                                            string={
                                                messageData.message
                                                    ? messageData.message
                                                    : ''
                                            }
                                            min={10}
                                            limit={50}
                                        />
                                    </>
                                )} */}
                            <p className='note'>
                                Note:{' '}
                                <span>The message will be sent to all the customers in this particular trip date</span>
                            </p>
                            <div className='alignCenter'>
                                <button className='dialogActBtn cancel' onClick={() => setModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className='dialogActBtn submit'
                                    disabled={!messageData.msgType}
                                    onClick={() =>
                                        sendMessage(
                                            {
                                                ...messageData,
                                                msgType: 'whatsapp',
                                                packDateId: rowData.packDateId
                                            },
                                            setLoading,
                                            setModal
                                        )
                                    }
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </Dialog>
                )}
            </div>
        </>
    );
};

export default SupBooking;
